import { AssetVizController, AssetVizSceneConfig } from 'features/assetViz'
import { IndicationStats } from 'features/indicationTriage'

export interface SavedIndicationGraph {
  indicationId: string
  timestamp: string
  graphTypes: {
    ascan: boolean
    bscan: boolean
    cscan: boolean
    dscan: boolean
  }
  paths: {
    ascan?: string
    bscan?: string
    cscan?: string
    dscan?: string
    /**
     * @deprecated Rendered images are now auto-generated by the processing pipeline
     * and stored on the indication entry at graphs/rendered-visualization-{metric|imperial}.png
     */
    renderedImage?: string
  }
  stats?: IndicationStats
  selection?: {
    min: {
      x: number
      y: number
    }
    max: {
      x: number
      y: number
    }
    x_measurement_unit: string
    y_measurement_unit: string
  }
  dra_rating: string
  sub_rating?: string
}

export interface SavedWeldIndicationGraph {
  imagePath: string
  timestamp: string
}

import {
  Branded,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoInspectionType,
  GeckoReportingMetadataJSON,
  GeckoUnit,
  GeckoVisualInspection,
  SignatureData,
  SignoffState,
} from 'types'

export type CombinedInspection =
  | (GeckoInspection & { kind: 'NDT' })
  | (GeckoVisualInspection & { kind: 'Visual' })

// ============================================================================
// Revision & Metadata Types
// ============================================================================

/**
 * Metadata about a report revision
 */
export interface RevisionMetadata {
  revisionNumber: number
  createdAt: string
  publishedAt?: string
  isPublished: boolean
}

/**
 * Option item for revision selection
 */
export interface RevisionOption {
  id: string
  revisionNumber: number
  createdAt: string
  publishedAt?: string
  isPublished: boolean
}

// Section string identifiers
export type ReportSectionId =
  | 'cover'
  | 'summary'
  | 'calibration'
  | 'indications'
  | 'clientImages'
  | 'assetDrawing'
  | 'review'
  | 'generate'

// Field path type for validation
export type NestedKeys = string | string[]

// Main section interface
export interface ReportSection {
  id: string
  title: string
  description: string
  required: boolean
  RenderReact: React.FC
  RenderPDF: React.FC<PdfProps>
  requiredFields: NestedKeys[]
  /** Whether to show this section in the Table of Contents. Defaults to true. */
  showInTOC?: boolean
}

// ============================================================================
// Form & State Types
// ============================================================================

/**
 * Asset drawings data structure
 */
export interface AssetDrawingsData {
  [key: string]: {
    [key: string]: {
      imagePath: string
      imageCaption?: string
      index?: number
      type:
        | 'heatmap'
        | 'no-heatmap'
        | 'heatmap-with-indications'
        | 'welds-no-heatmap'
        | string
    }
  }
}

// ============================================================================
// Component & Feature Types
// ============================================================================

/**
 * Section selection for copying revision content
 */
export type SectionSelection = { [key: string]: boolean }

export interface MenuItem {
  id: ReportSection
  title: string
  description: string
  required: boolean
  status?: {
    isComplete: boolean
    completedFields: number
    totalFields: number
  }
}

// ============================================================================
// Report Template Types
// ============================================================================

export interface TOCSection {
  id: string
  RenderPDF: React.FC<TOCSectionProps>
  RenderReact?: React.FC
  title: string
}

export type TOCSectionProps = {
  entries: Array<{
    id: string
    title: string
  }>
  sectionPages: Record<string, number>
  onRender: (info: { id: string; pageNumber: number }) => void
}

export interface PDFGenerationSection {
  id: 'pdfGeneration'
  title: 'Generate PDF'
  description: 'Generate and download final report'
  RenderReact: React.FC
  required: boolean
}

export type ReviewSectionProps = {
  previewMode: boolean
}

export interface ReviewSection {
  id: 'review'
  title: 'Review'
  description: 'Review report data and complete sign off'
  RenderReact: React.FC<ReviewSectionProps>
  required: true
}

export type PdfProps = {
  onRender: (info: { id: string; pageNumber: number }) => void
}

export interface StaticSection {
  RenderPDF: React.FC<PdfProps>
  id: string
  title: string
}

export type Report = {
  coverSection?: ReportSection
  tocSection: TOCSection
  sections: ReportSection[]
  reviewSection: ReviewSection
  pdfGenerationSection: PDFGenerationSection
  staticSections: StaticSection[]
}

export type ReportInspectionType =
  | GeckoInspectionType
  | 'external-visual'
  | 'internal-visual'

export interface BaseFormData {
  signoff?: SignoffState
  revisionMetadata: RevisionMetadata
  lockState?: {
    isLocked: boolean
    startedSignoff: boolean
    lastLockTimestamp?: string
  }
  lock_state?: {
    isLocked: boolean
    startedSignoff: boolean
    lastLockTimestamp?: string
  }
  publishedPdfPath?: string
}

export interface BaseContextValue<TSectionSelection = SectionSelection> {
  // Navigation
  activePage: string | null
  report: Report
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  completionStatus: Record<
    string,
    {
      isComplete: boolean
      completedFields: number
      totalFields: number
    }
  >

  // Form Data
  formData: BaseFormData
  updateFormData: (newData: Partial<BaseFormData>) => void
  handleSignoffFinish: (values: SignatureData) => Promise<SignoffState>
  revertFormToCantilever: () => Promise<void>

  // Metadata
  metadataEntryId: string
  isLoading: boolean
  currentRevisionNumber: number
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
  unit: GeckoUnit | undefined

  // Submissions
  isSubmitting: boolean
  handleSubmitReport: () => Promise<void>
  handleSignoff: (signatureData: SignatureData) => Promise<void>
  handlePublishRevision: () => Promise<void>

  // Revisions
  revisions: RevisionOption[]
  selectedRevisionData?: BaseFormData
  loadRevisionData: (revisionId: string) => Promise<void>
  copyRevisionContent: (sections: TSectionSelection) => Promise<void>
  hasPublishedRevisions: boolean
  canRevertToCantilever: boolean
  createNewRevision: () => Promise<void>
  isCurrentRevisionPublished: boolean

  // Visualization, optional since not all reports require asset visualization
  assetVizRefs?: {
    indicationsVizRef: React.RefObject<AssetVizController | null>
    drawingVizRef: React.RefObject<AssetVizController | null>
  }
  sceneConfigs?: {
    baseSceneConfig: AssetVizSceneConfig
    heatmapSceneConfig: AssetVizSceneConfig
    noHeatmapSceneConfig: AssetVizSceneConfig
  }

  // Cantilever Data
  hasCantileverData: boolean

  // Lock Management
  lockState: {
    isLocked: boolean
    startedSignoff: boolean
    lastLockTimestamp?: string
  }
  handleStartSignoff: () => Promise<void>
  handleUnlock: () => Promise<void>
  canStartSignoff: boolean
  canSignAsReviewer: boolean

  // PDF Management
  pdfState: {
    hasPublishedPdf: boolean
    latestDraftTimestamp?: string
    publishedPdfPath?: string
  }
  publishLatestDraftPDF: () => Promise<void>
  handleDownloadLatestDraft: () => Promise<void>
  handleDownloadPublishedPdf: () => Promise<void>
}

/**
 * Report template that specifies what the template supports and what context data is required
 */
export interface ReportTemplate {
  supportedComponentTypes: GeckoComponentModelType[]
  supportedInspectionTypes: ReportInspectionType[]
  canSupportMultipleInspections: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reportContext: React.Context<any> // Allow any context that extends BaseContextValue
  providerComponent: React.ComponentType<{
    children: React.ReactNode
    reportID: string
  }>
  reportName: string
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for RevisionMetadata
 */
export function isRevisionMetadata(obj: unknown): obj is RevisionMetadata {
  if (!obj || typeof obj !== 'object') return false
  return 'revisionNumber' in obj && 'createdAt' in obj && 'isPublished' in obj
}

/**
 * Type guard for GeckoReportingMetadataJSON
 */
export function isGeckoReportingMetadataJSON(
  obj: unknown
): obj is GeckoReportingMetadataJSON {
  if (!obj || typeof obj !== 'object') return false
  return (
    'main_info' in obj &&
    'cover_page' in obj &&
    'summary_page' in obj &&
    'calibration_page' in obj &&
    'revisionMetadata' in obj
  )
}

export type ReportGenerationId = Branded<string, 'ReportGenerationId'>

// ============================================================================
// Utility Functions
// ============================================================================

export function getRequiredSections(report: Report): string[] {
  const requiredSections: string[] = []

  // Add cover section if it exists and is required
  if (report.coverSection?.required) {
    requiredSections.push(report.coverSection.id)
  }

  // Add main sections that are required
  requiredSections.push(
    ...report.sections
      .filter((section) => section.required)
      .map((section) => section.id)
  )

  return requiredSections
}

export function getReportSections(
  report: Report
): (ReportSection | ReviewSection | PDFGenerationSection)[] {
  const sections: (ReportSection | ReviewSection | PDFGenerationSection)[] = []

  // Add cover section if it exists
  if (report.coverSection) {
    sections.push(report.coverSection)
  }

  sections.push(
    ...report.sections,
    report.reviewSection,
    report.pdfGenerationSection
  )

  return sections
}

export function genSectionMap(
  report: Report
): Record<string, ReportSection | ReviewSection | undefined> {
  const sectionMap: Record<string, ReportSection | ReviewSection> = {}

  // Add cover section if it exists
  if (report.coverSection) {
    sectionMap[report.coverSection.id] = report.coverSection
  }

  // Add main sections
  Object.assign(
    sectionMap,
    Object.fromEntries(report.sections.map((section) => [section.id, section]))
  )

  // Add review section
  sectionMap[report.reviewSection.id] = report.reviewSection

  return sectionMap
}
